/**
 * ReviewQueueRowContainer — resolves per-row data for one Review-queue row
 * (SE-5499, restyled SE-6554).
 *
 * A `PipelineReviewRequest` carries only a `playerId` (no nested player), and
 * there is no batch player-by-ids query, so each row resolves its own player
 * name via `PipelineReviewQueuePlayer` (getPlayer). This is an intentional
 * React-level N+1: InMemoryCache normalizes each `getPlayer(id)` by Player.id,
 * so repeated playerIds across rows collapse to one network fetch.
 *
 * ## What the card can honestly show (SE-6554)
 *
 * The design's card carries a signals row of GPR, "N reports", "Starting XI"
 * and "Youth". NONE of the four has a source on this surface: the row model is
 * built from `PipelineReviewRequestFragment` (no player stats at all), and the
 * per-row `PipelineReviewQueuePlayer` read selects identity + team + positions
 * only. The board card's honest reports/verdict pair
 * ({@link "@/features/player-kanban/utils/cardReportSignal"}) comes from
 * `ListKanbanCards`, which this surface does not run — wiring it here means
 * widening the per-row query, i.e. a schema/codegen change, not a restyle.
 *
 * So this container derives the signals the data DOES carry — the producer, its
 * attribution, the proposed transition, the request's age, the player's team and
 * position, and any proposed terminal outcome — and renders nothing where the
 * design would have shown a number nobody measured.
 * @remarks Container component — owns the per-row query + label derivation.
 */

import { useCallback } from "react";

import {
  PipelineReviewSource,
  PipelineReviewStatus,
  usePipelineReviewQueuePlayerQuery,
} from "@/generated/graphql";
import type { PillTone } from "@/components/atoms/Badge";

import { formatRelativeAgo } from "../../../utils/cardUpdatedAgo";
import { outcomeLabel } from "../../../utils/outcomeLabels";
import type { ReviewQueueRow } from "../../../utils/reviewQueueRows";
import ReviewQueueRowView from "./ReviewQueueRowView";

/** Props for {@link ReviewQueueRowContainer}. */
export interface ReviewQueueRowContainerProps {
  /** The row model. */
  readonly row: ReviewQueueRow;
  /** Whether an accept/decline is in flight for this row. */
  readonly isDeciding: boolean;
  /** Accept handler (receives the row). */
  readonly onAccept: (row: ReviewQueueRow) => void;
  /** Decline handler (receives the row). */
  readonly onDecline: (row: ReviewQueueRow) => void;
}

/** Human-readable status labels. */
const STATUS_LABEL: Record<PipelineReviewStatus, string> = {
  [PipelineReviewStatus.Pending]: "Pending",
  [PipelineReviewStatus.Accepted]: "Accepted",
  [PipelineReviewStatus.Declined]: "Declined",
};

/** Status → badge tone. */
const STATUS_TONE: Record<PipelineReviewStatus, "muted" | "success" | "error"> =
  {
    [PipelineReviewStatus.Pending]: "muted",
    [PipelineReviewStatus.Accepted]: "success",
    [PipelineReviewStatus.Declined]: "error",
  };

/** Human-readable source labels. */
const SOURCE_LABEL: Record<PipelineReviewSource, string> = {
  [PipelineReviewSource.AutomationRule]: "Automation",
  [PipelineReviewSource.Manual]: "Manual",
  [PipelineReviewSource.ScoutFlag]: "Scout flag",
};

/**
 * SE-6554 origin badge label — the SHORT form the design prints in the pill
 * (the fuller {@link SOURCE_LABEL} still drives the filter pills and the
 * row's accessibility label).
 */
const ORIGIN_LABEL: Record<PipelineReviewSource, string> = {
  [PipelineReviewSource.AutomationRule]: "Rule",
  [PipelineReviewSource.Manual]: "Manual",
  [PipelineReviewSource.ScoutFlag]: "Scout",
};

/**
 * SE-6554 origin badge tone. The design draws an automation rule amber and a
 * scout flag light blue; a manual request has no colour in the design (it is
 * not in the v1 frame at all), so it takes the neutral outline rather than
 * borrowing a status colour it has not earned.
 */
const ORIGIN_TONE: Record<PipelineReviewSource, PillTone> = {
  [PipelineReviewSource.AutomationRule]: "warning-subtle",
  [PipelineReviewSource.Manual]: "neutral-outline",
  [PipelineReviewSource.ScoutFlag]: "info-subtle",
};

/**
 * The line beside the origin badge: WHO/WHAT asked, in the producer's own
 * words where there are any.
 *
 * An automation rule is named (`by rule "…"`) because the rule is the actor. A
 * scout flag or manual request is a person's judgement, and the only trace of
 * that person on this row is the free-text `requestReason` — so the reason is
 * quoted when present. The design shows a scout NAME alongside the quote;
 * `PipelineReviewRequest` exposes no requester identity, so the quote stands
 * alone rather than being attributed to someone the row cannot name.
 * @param row - The row model.
 * @returns The attribution line.
 */
const buildAttribution = (row: ReviewQueueRow): string => {
  if (row.source === PipelineReviewSource.AutomationRule) {
    return `by rule "${row.ruleName}"`;
  }
  const reason = row.requestReason?.trim();
  return reason ? `"${reason}"` : row.ruleName;
};

/**
 * The proposed move, as one line.
 *
 * A request with no target stage is not a move — it is a flag raised against
 * the pipeline itself, which is what a scout flag usually is — so it says so
 * rather than rendering an arrow into an em-dash.
 * @param row - The row model.
 * @returns The transition line.
 */
const buildTransition = (row: ReviewQueueRow): string => {
  if (row.targetPhaseName === null) return "Flagged for this pipeline";
  return `${row.fromPhaseName ?? "—"} → ${row.targetPhaseName}`;
};

/**
 * Container for a single Review-queue row.
 * @param props - {@link ReviewQueueRowContainerProps}
 * @param props.row - The row model
 * @param props.isDeciding - Whether an accept/decline is in flight for this row
 * @param props.onAccept - Accept handler (receives the row)
 * @param props.onDecline - Decline handler (receives the row)
 * @returns The wired {@link ReviewQueueRowView}.
 */
const ReviewQueueRowContainer = ({
  row,
  isDeciding,
  onAccept,
  onDecline,
}: ReviewQueueRowContainerProps) => {
  const { data, loading } = usePipelineReviewQueuePlayerQuery({
    variables: { id: row.playerId },
  });

  const player = data?.getPlayer;
  const playerName =
    player?.knownName?.trim() ||
    player?.name?.trim() ||
    [player?.firstName, player?.lastName].filter(Boolean).join(" ").trim() ||
    null;

  /* The two context signals the per-row read actually selects. Joined into one
     chip because they read as a single "who this is" phrase, and an empty
     string tells the View to render no chip at all. */
  const position = player?.positions?.[0]?.abbreviation?.trim() ?? "";
  const team = player?.teamName?.trim() ?? "";
  const contextLabel = [position, team].filter(Boolean).join(" · ");

  const handleAccept = useCallback(() => onAccept(row), [onAccept, row]);
  const handleDecline = useCallback(() => onDecline(row), [onDecline, row]);

  return (
    <ReviewQueueRowView
      testID={`review-queue-row-${row.id}`}
      ruleName={row.ruleName}
      playerName={playerName}
      playerLoading={loading}
      fromPhaseName={row.fromPhaseName}
      targetPhaseName={row.targetPhaseName}
      sourceLabel={SOURCE_LABEL[row.source]}
      originLabel={ORIGIN_LABEL[row.source]}
      originTone={ORIGIN_TONE[row.source]}
      attribution={buildAttribution(row)}
      transitionLabel={buildTransition(row)}
      ageLabel={formatRelativeAgo(row.createdAt) ?? ""}
      contextLabel={contextLabel}
      outcomeLabel={
        row.requestedOutcome === null ? "" : outcomeLabel(row.requestedOutcome)
      }
      statusLabel={STATUS_LABEL[row.status]}
      statusTone={STATUS_TONE[row.status]}
      isPending={row.isPending}
      isResolved={row.isResolved}
      isDeciding={isDeciding}
      onAccept={handleAccept}
      onDecline={handleDecline}
    />
  );
};

ReviewQueueRowContainer.displayName = "ReviewQueueRowContainer";

export default ReviewQueueRowContainer;
